import React, { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowUpRight } from "@phosphor-icons/react";

/**
 * MoodPlayer — a "now playing"-style widget that morphs through
 * mood labels (Soju o'clock, Glow loading, Beauty ritual, After hours).
 * The active label can be clicked to filter the catalogue.
 */
export default function MoodPlayer({ moods = [], interval = 2800, onPick }) {
  const [i, setI] = useState(0);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    if (moods.length < 2) return;
    setProgress(0);
    const start = performance.now();
    let raf = 0;
    const tick = (t) => {
      const p = Math.min(1, (t - start) / interval);
      setProgress(p);
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    const t = setTimeout(() => setI((v) => (v + 1) % moods.length), interval);
    return () => { clearTimeout(t); cancelAnimationFrame(raf); };
  }, [i, moods.length, interval]);

  if (!moods.length) return null;
  const active = moods[i];
  const next = moods[(i + 1) % moods.length];

  return (
    <div
      data-testid="mood-player"
      className="relative w-[260px] sm:w-[280px] glass rounded-2xl px-4 py-3.5 shadow-[0_18px_40px_-18px_rgba(15,23,42,0.18)]"
      style={{ overflow: "hidden" }}
    >
      {/* color wash that morphs with active mood */}
      <div
        aria-hidden
        className="absolute inset-0 -z-10 opacity-60 transition-colors duration-700"
        style={{ background: active.bg || "transparent" }}
      />

      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="inline-flex items-center gap-2 text-[10px] tracking-[0.28em] uppercase font-display">
          <span className="relative flex h-1.5 w-1.5">
            <span className="absolute inline-flex h-full w-full rounded-full bg-[#FF6B9E] opacity-75 animate-ping" />
            <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-[#FF6B9E]" />
          </span>
          Now playing
        </div>
        <span className="text-[9px] tracking-[0.28em] uppercase font-display text-slate-400">
          {String(i + 1).padStart(2, "0")} / {String(moods.length).padStart(2, "0")}
        </span>
      </div>

      {/* Active mood — letters stagger in */}
      <button
        type="button"
        onClick={() => onPick && onPick(active)}
        data-testid={`mood-${active.slug}`}
        className="mt-2 w-full text-left group"
      >
        <div className="flex items-baseline gap-2.5">
          <span className="text-3xl">{active.emoji}</span>
          <AnimatePresence mode="wait">
            <motion.div
              key={active.label}
              initial="enter"
              animate="visible"
              exit="exit"
              variants={{
                enter:   { transition: { staggerChildren: 0.03 } },
                visible: { transition: { staggerChildren: 0.03 } },
                exit:    { transition: { staggerChildren: 0.02, staggerDirection: -1 } },
              }}
              className="overflow-hidden font-display text-[22px] font-medium tracking-tight leading-tight flex flex-wrap"
            >
              {active.label.split("").map((ch, idx) => (
                <motion.span
                  key={idx}
                  variants={{
                    enter:   { opacity: 0, y: "0.4em", filter: "blur(8px)" },
                    visible: { opacity: 1, y: 0, filter: "blur(0px)", transition: { duration: 0.45, ease: [0.22, 1, 0.36, 1] } },
                    exit:    { opacity: 0, y: "-0.4em", filter: "blur(8px)", transition: { duration: 0.3, ease: [0.55, 0, 0.7, 0.2] } },
                  }}
                  className="inline-block"
                >
                  {ch === " " ? "\u00A0" : ch}
                </motion.span>
              ))}
            </motion.div>
          </AnimatePresence>
        </div>
        {/* Sublabel */}
        <AnimatePresence mode="wait">
          <motion.div
            key={active.sublabel || ""}
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -4 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="mt-1.5 text-[10px] tracking-[0.22em] uppercase text-slate-600"
          >
            {active.sublabel}
          </motion.div>
        </AnimatePresence>
        <div className="mt-1.5 inline-flex items-center gap-1 text-[10px] tracking-[0.28em] uppercase text-slate-400 group-hover:text-[#0F172A] transition-colors">
          Tap to filter <ArrowUpRight size={11} weight="bold" className="group-hover:rotate-45 transition-transform" />
        </div>
      </button>

      {/* Progress bar */}
      <div className="mt-3 h-[2px] bg-slate-200 rounded-full overflow-hidden">
        <div
          className="h-full bg-gradient-to-r from-[#FF6B9E] via-[#A78BFA] to-[#38BDF8]"
          style={{ width: `${progress * 100}%`, transition: "width 60ms linear" }}
        />
      </div>

      {/* Up next */}
      <div className="mt-3 flex items-center justify-between text-[10px] tracking-[0.28em] uppercase">
        <span className="text-slate-400">Up next</span>
        <span className="font-display text-slate-700 inline-flex items-center gap-1.5">
          <span className="text-base leading-none">{next.emoji}</span>
          {next.label}
        </span>
      </div>
    </div>
  );
}
